import { apiClient } from "@/lib/api-client"
import { HttpStatusCodes } from "@/lib/http-status-codes"
import { ApplicationListItem } from "./dtos/ApplicantListItem"
import { ApplicationDetails } from "./dtos/ApplicationDetails"
import { CreateExpertInterviewCommand } from "./dtos/CreateExpertInterviewCommand"
import { InterviewQuestion } from "@/modules/public/dto/videoInterviewDto"
import { CreateVideoInterviewCommand } from "./dtos/CreateVideoInterviewCommand"
import { IdVerificationResult } from "./dtos/IdVerificationDto"
import { AcceptedCvUploadDto } from "@/modules/recruitment/candidates/dtos/cv-upload"

export const GetAllApplicantsForVacancyId = async (
  organizationId: string,
  vacancyId: string,
): Promise<ApplicationListItem[]> => {
  const response = await apiClient.get(
    `/organizations/${organizationId}/vacancies/${vacancyId}/applications`,
  )
  return response.data
}

export const GetApplicationsForVacancy = async (
  organizationId: string,
  vacancyId: string,
): Promise<ApplicationDetails[]> => {
  const response = await apiClient.get(
    `/organizations/${organizationId}/applications`,
    {
      params: {
        jobId: vacancyId,
      },
    },
  )
  return response.data
}

export const DeleteApplicantById = async (
  applicantId: string,
): Promise<void> => {
  const response = await apiClient.delete(`/applicants/${applicantId}`)
  if (!response.status || response.status !== HttpStatusCodes.OK) {
    throw new Error("Failed to delete applicant")
  }
}

export const createScreeningCall = async (
  organizationId: string,
  applicationId: string,
): Promise<void> => {
  const response = await apiClient.post(
    `/organizations/${organizationId}/interview`,
    { applicationId },
  )
  return response.data
}

export const reconcileInterview = async (
  organizationId: string,
  interviewId: string,
): Promise<void> => {
  await apiClient.post(
    `/organizations/${organizationId}/interview/${interviewId}/reconcile`,
  )
}

export const GetApplicationDetails = async (
  organizationId: string,
  applicationId: string,
): Promise<ApplicationDetails> => {
  const response = await apiClient.get(
    `/organizations/${organizationId}/applications/${applicationId}`,
  )
  return response.data
}

export const createVideoInterview = async (
  organizationId: string,
  command: CreateVideoInterviewCommand,
): Promise<void> => {
  await apiClient.post(
    `/organizations/${organizationId}/interview/video`,
    command,
  )
}

export const createExpertInterview = async (
  organizationId: string,
  command: CreateExpertInterviewCommand,
): Promise<void> => {
  await apiClient.post(
    `/organizations/${organizationId}/interview/expert`,
    command,
  )
}

export const uploadCv = async (
  organizationId: string,
  vacancyId: string,
  files: File[],
  onUploadProgress?: (percent: number) => void,
): Promise<AcceptedCvUploadDto[]> => {
  const formData = new FormData()
  formData.append("OrganizationId", organizationId)
  formData.append("vacancyId", vacancyId)
  files.forEach((file) => formData.append("cvFiles", file))

  const response = await apiClient.post<AcceptedCvUploadDto[]>(
    `/organizations/${organizationId}/candidates/upload-cv`,
    formData,
    {
      onUploadProgress: onUploadProgress
        ? (e) => {
            const percent = e.total ? Math.round((e.loaded * 100) / e.total) : 0
            onUploadProgress(percent)
          }
        : undefined,
    },
  )
  return response.data
}

export const acceptCandidate = async (
    organizationId: string,
    applicationId: string,
    contractFile: File,
): Promise<void> => {
    const formData = new FormData()
    formData.append("ContractFile", contractFile)

    await apiClient.post(
        `/organizations/${organizationId}/applications/${applicationId}/accept`,
        formData,
    )
}

export const hireCandidate = async (
  applicationId: string,
  candidateId: string,
  contractFile: File,
    startDate: Date,
  workflowTemplateIds?: string[],
    documentsNeeded?: string[],
): Promise<void> => {
  const formData = new FormData()
  formData.append("ApplicationId", applicationId)
  formData.append("CandidateId", candidateId)
  formData.append("Contract", contractFile)

    formData.append("StartDate", startDate.toISOString())

  workflowTemplateIds?.forEach((id) =>
    formData.append("WorkflowTemplateIds", id),
  )

  documentsNeeded?.forEach((doc) =>
    formData.append("DocumentsNeeded", doc),
  )

  await apiClient.post("/onboarding/hire-candidate", formData)
}

export const rejectCandidate = async (
    organizationId: string,
    applicationId: string,
    reason: string,
): Promise<void> => {
    await apiClient.post(
        `/organizations/${organizationId}/applications/${applicationId}/reject`,
        {
            reason,
        },
    )
}

export interface BulkRejectionJobDto {
  jobId: string
  total: number
  processed: number
  status: "Pending" | "InProgress" | "Completed" | "Failed"
}

export interface BulkRejectionProgressDto {
  jobId: string
  total: number
  processed: number
  status: "Pending" | "InProgress" | "Completed" | "Failed"
}

export const bulkRejectCandidates = async (
  organizationId: string,
  applicationIds: string[],
  reason?: string,
): Promise<BulkRejectionJobDto> => {
  const response = await apiClient.post(
    `/organizations/${organizationId}/applications/bulk-reject`,
    { applicationIds, reason },
  )
  return response.data
}

export interface BulkVideoInterviewJobDto {
  jobId: string
  total: number
  processed: number
  status: "Pending" | "InProgress" | "Completed" | "Failed"
}

export interface BulkVideoProgressDto {
  jobId: string
  total: number
  processed: number
  status: "Pending" | "InProgress" | "Completed" | "Failed"
}

export const bulkCreateVideoInterviews = async (
  organizationId: string,
  applicationIds: string[],
  questions: Partial<InterviewQuestion>[],
  videoDeadline?: Date,
  maxVideoAttempts?: number,
): Promise<BulkVideoInterviewJobDto> => {
  const response = await apiClient.post(
    `/organizations/${organizationId}/applications/bulk-video-interviews`,
    {
      applicationIds,
      questions,
      videoDeadline,
      maxVideoAttempts,
    },
  )
  return response.data
}

export type BulkAiCallCandidateStatus = "Queued" | "Dispatched" | "Failed"

export interface BulkAiCallCandidateDto {
  applicationId: string
  candidateName: string
  status: BulkAiCallCandidateStatus
  failureReason?: string | null
}

export interface BulkAiCallJobDto {
  jobId: string
  total: number
  processed: number
  failed: number
  status: "Pending" | "InProgress" | "Completed" | "Failed"
  candidates?: BulkAiCallCandidateDto[]
}

export type BulkAiCallProgressDto = BulkAiCallJobDto

export interface BulkAiCallCandidateProgressDto {
  jobId: string
  applicationId: string
  candidateName: string
  status: BulkAiCallCandidateStatus
  failureReason?: string | null
}

export interface CallQuotaStatusDto {
  limit: number
  used: number
  remaining: number
  windowStartedAt: string | null
  resetsAt: string | null
}

export const bulkCreateAiCalls = async (
  organizationId: string,
  applicationIds: string[],
): Promise<BulkAiCallJobDto> => {
  const response = await apiClient.post(
    `/organizations/${organizationId}/interview/bulk`,
    { applicationIds },
  )
  return response.data
}

export const getCallQuota = async (
  organizationId: string,
): Promise<CallQuotaStatusDto> => {
  const response = await apiClient.get(
    `/organizations/${organizationId}/interview/call-quota`,
  )
  return response.data
}

export const updateInterviewNotes = async (
    organizationId: string,
    interviewId: string,
    notes: string,
): Promise<void> => {
    await apiClient.put(
        `/organizations/${organizationId}/interview/${interviewId}/notes`,
        { notes },
    )
}

export const getQuestions = async (
    organizationId: string,
    searchTerm?: string,
): Promise<InterviewQuestion[]> => {
    const response = await apiClient.get(
        `/organizations/${organizationId}/questions`,
        {
            params: {
                searchTerm,
            },
        },
    )
    return response.data
}

export const runIdVerification = async (
    organizationId: string,
    candidateId: string,
    idNumber: string,
    forceRerun = false,
): Promise<IdVerificationResult> => {
    const response = await apiClient.post(
        `/organizations/${organizationId}/candidates/${candidateId}/background-checks/id-verification`,
        { idNumber, forceRerun },
    )
    return response.data
}

export const getIdVerification = async (
    organizationId: string,
    candidateId: string,
): Promise<IdVerificationResult | null> => {
    const response = await apiClient.get(
        `/organizations/${organizationId}/candidates/${candidateId}/background-checks/id-verification`,
    )
    return response.data
}

export const generateAiQuestions = async (organizationId: string, vacancyId: string): Promise<Partial<InterviewQuestion>[]> => {
    try {
        const response = await apiClient.post(`/organizations/${organizationId}/vacancies/${vacancyId}/generate-questions`)
        return response.data;
    } catch (error: unknown) {
        if (error && typeof error === 'object' && 'structuredError' in error) {
            const err = error as { structuredError: { message?: string } };
            console.error(err.structuredError);
            throw new Error(err.structuredError.message || "Failed to generate questions");
        }
        throw error;
    }
}